import { useMemo } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { z } from 'zod';

import { defaultDateRange } from '@/lib/date-range';
import {
  formatCompactCount,
  formatCurrencyExact,
  formatPercent,
} from '@/lib/format';
import { AdminPageHeader } from '@/components/admin/admin-page-header';
import { DailyCostChart } from '@/components/admin/llm/daily-cost-chart';
import { LlmDateRangePicker } from '@/components/admin/llm/llm-date-range-picker';
import { marginTotals } from '@/components/admin/llm/margin';
import { LLM_METRIC_HINTS } from '@/components/admin/llm/metric-hints';
import { PerModelTable } from '@/components/admin/llm/per-model-table';
import { PerOrgTable } from '@/components/admin/llm/per-org-table';
import { WindowLabel } from '@/components/admin/llm/window-label';
import { StatTile } from '@/components/admin/shared/stat-tile';
import { useAdminLlmUsageSummary } from '@/hooks/data/admin/use-llm';
import type { AdminLLMUsageSummaryRead } from '@/types/generated';
import { SECTION_TITLE } from '@/components/ui/typography';
import { cn } from '@/lib/utils';

const summaryTotals = (summary: AdminLLMUsageSummaryRead) => ({
  ...summary.per_org.reduce(
    (acc, row) => ({
      cost: acc.cost + Number(row.cost_usd),
      calls: acc.calls + row.call_count,
      tokens: acc.tokens + row.input_tokens + row.output_tokens,
    }),
    { cost: 0, calls: 0, tokens: 0 },
  ),
  margin: marginTotals(summary.per_org),
});

const searchSchema = z.object({
  start: z.string().date().optional(),
  end: z.string().date().optional(),
});

export const Route = createFileRoute('/_authenticated/admin/llm-usage/')({
  validateSearch: searchSchema,
  staticData: { title: 'LLM usage' },
  component: AdminLlmUsagePage,
});

function AdminLlmUsagePage(): React.ReactElement {
  const { start, end } = Route.useSearch();
  const navigate = Route.useNavigate();

  const defaultRange = useMemo(() => defaultDateRange(30), []);
  const rangeStart = start ?? defaultRange.start;
  const rangeEnd = end ?? defaultRange.end;

  const summaryQuery = useAdminLlmUsageSummary({
    start: rangeStart,
    end: rangeEnd,
  });
  const summary = summaryQuery.data;

  const handleApply = (range: { start?: string; end?: string }): void => {
    void navigate({
      search: (s) => ({
        ...s,
        start: range.start,
        end: range.end,
      }),
    });
  };

  return (
    <main>
      <AdminPageHeader
        title="LLM usage"
        description="Cross-org LLM cost and usage breakdown"
      />

      <div className="flex flex-col gap-8 px-4 py-6 sm:px-8">
        {summary && (
          <WindowLabel start={summary.window_start} end={summary.window_end} />
        )}

        <LlmDateRangePicker
          key={`${rangeStart}-${rangeEnd}`}
          start={rangeStart}
          end={rangeEnd}
          onApply={handleApply}
        />

        {summaryQuery.isLoading && (
          <p className="text-fg-3 text-fs-13">Loading…</p>
        )}

        {summaryQuery.isError && (
          <p className="text-danger-700 text-fs-13">
            Failed to load LLM usage data.
          </p>
        )}

        {summary && (
          <>
            <div className="grid grid-cols-2 gap-4 @3xl:grid-cols-3">
              <StatTile
                label="Total cost"
                value={formatCurrencyExact(summaryTotals(summary).cost)}
                hint="Estimated USD cost across all organizations over the window."
              />
              <StatTile
                label="Credits charged"
                value={formatCompactCount(
                  summaryTotals(summary).margin.credits,
                )}
                hint={LLM_METRIC_HINTS.credits}
              />
              <StatTile
                label="Margin"
                value={formatCurrencyExact(
                  summaryTotals(summary).margin.margin,
                )}
                hint={LLM_METRIC_HINTS.margin}
                sub={
                  summaryTotals(summary).margin.pct === null
                    ? 'No credits charged'
                    : `${formatPercent(summaryTotals(summary).margin.pct)} of credit value`
                }
                tone={
                  summaryTotals(summary).margin.margin < 0
                    ? 'danger'
                    : 'default'
                }
              />
              <StatTile
                label="Total calls"
                value={formatCompactCount(summaryTotals(summary).calls)}
                hint="Total LLM API calls across all organizations."
              />
              <StatTile
                label="Total tokens"
                value={formatCompactCount(summaryTotals(summary).tokens)}
                hint="Input + output tokens across all organizations."
              />
              <StatTile
                label="Active orgs"
                value={formatCompactCount(summary.per_org.length)}
                hint="Organizations with at least one LLM call over the window."
              />
            </div>

            <section>
              <h2 className={cn(SECTION_TITLE, 'mb-3')}>Daily cost</h2>
              <DailyCostChart data={summary.daily} />
            </section>

            <section>
              <h2 className={cn(SECTION_TITLE, 'mb-3')}>Per organization</h2>
              <PerOrgTable
                data={summary.per_org}
                start={rangeStart}
                end={rangeEnd}
              />
            </section>

            <section>
              <h2 className={cn(SECTION_TITLE, 'mb-3')}>Per model</h2>
              <PerModelTable data={summary.per_model} />
            </section>
          </>
        )}
      </div>
    </main>
  );
}
